import React, { useState } from 'react';
import { TextField, Button, MenuItem } from '@mui/material';
import DrawerShell from './DrawerShell';
import { FieldGroup } from '../PanelComerciales.styles';
import { panelTheme } from '../PanelComerciales.theme';
import { Commercial, VipAccount } from '../PanelComerciales.types';
import { isEmail, isNonEmptyTeamName, isRequired } from '../utils/validators';

interface AssignVipDrawerProps {
    open: boolean;
    onClose: () => void;
    commercials: Commercial[];
    existingEmails: string[];
    onSave: (vip: VipAccount) => void;
}

const AssignVipDrawer: React.FC<AssignVipDrawerProps> = ({
    open,
    onClose,
    commercials,
    existingEmails,
    onSave,
}) => {
    const [client, setClient] = useState('');
    const [email, setEmail] = useState('');
    const [ownerId, setOwnerId] = useState<number | ''>('');
    const [reason, setReason] = useState('Estratégico');
    const [validity, setValidity] = useState('Permanente');
    const [error, setError] = useState<string | null>(null);

    const close = () => {
        setClient('');
        setEmail('');
        setOwnerId('');
        setReason('Estratégico');
        setValidity('Permanente');
        setError(null);
        onClose();
    };

    const submit = () => {
        if (!isNonEmptyTeamName(client)) return setError('Cliente requerido');
        if (!isEmail(email)) return setError('Email inválido');
        if (existingEmails.some((e) => e.toLowerCase() === email.trim().toLowerCase())) {
            return setError('Ya existe un VIP con ese email');
        }
        if (!ownerId) return setError('Selecciona un comercial');
        if (!isRequired(reason)) return setError('Motivo requerido');
        if (!isRequired(validity)) return setError('Vigencia requerida');

        const owner = commercials.find((c) => c.id === ownerId);
        if (!owner) return setError('Comercial no encontrado');

        onSave({
            id: `vip-${Date.now()}`,
            client: client.trim(),
            email: email.trim(),
            owner: owner.name,
            team: owner.team,
            reason: reason.trim(),
            validity: validity.trim(),
            status: 'Activo',
        });
        close();
    };

    return (
        <DrawerShell
            open={open}
            title='Asignar cuenta VIP'
            description='Completa la información y guarda los cambios.'
            onClose={close}
        >
            <FieldGroup>
                <label>Buscar cliente</label>
                <TextField
                    size='small'
                    value={client}
                    onChange={(e) => setClient(e.target.value)}
                    placeholder='Nombre o PCOM ID'
                />
            </FieldGroup>
            <FieldGroup>
                <label>Email</label>
                <TextField
                    size='small'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder='correo@empresa.com'
                />
            </FieldGroup>
            <FieldGroup>
                <label>Owner fijo</label>
                <TextField
                    size='small'
                    select
                    value={ownerId}
                    onChange={(e) => setOwnerId(Number(e.target.value))}
                >
                    {commercials
                        .filter((c) => c.active)
                        .map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.name} ({c.team})
                            </MenuItem>
                        ))}
                </TextField>
            </FieldGroup>
            <FieldGroup>
                <label>Motivo</label>
                <TextField
                    size='small'
                    multiline
                    minRows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder='Razón de asignación VIP'
                />
            </FieldGroup>
            <FieldGroup>
                <label>Vigencia</label>
                <TextField
                    size='small'
                    select
                    value={validity}
                    onChange={(e) => setValidity(e.target.value)}
                >
                    <MenuItem value='Permanente'>Permanente</MenuItem>
                    <MenuItem value='30 días'>30 días</MenuItem>
                    <MenuItem value='60 días'>60 días</MenuItem>
                    <MenuItem value='90 días'>90 días</MenuItem>
                </TextField>
            </FieldGroup>
            {error ? <span style={{ color: panelTheme.colors.danger, fontSize: 12 }}>{error}</span> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button onClick={close} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    onClick={submit}
                    variant='contained'
                    sx={{
                        textTransform: 'none',
                        background: panelTheme.colors.indigo,
                        '&:hover': { background: panelTheme.colors.violet },
                    }}
                >
                    Asignar
                </Button>
            </div>
        </DrawerShell>
    );
};

export default AssignVipDrawer;
